import { Component, Element, Prop, State, Watch, Host, h, getAssetPath } from '@stencil/core';
import { Color } from '../../../interface';
import { createColorClasses } from '../../../utils/theme';
import isEmpty from 'lodash-es/isEmpty';
import { getIconMap } from './utils';

@Component({
  tag: 'lar-icon',
  styleUrl: 'icon.scss',
  assetsDirs: ['assets'],
  shadow: true
})

export class Icon {
  @Element() el!: HTMLElement;

  @State() private svgContent?: string;
  /**
   * The color to use from your application's color palette.
   * Default options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop() color?: Color;
  /**
   * Use 'named' incon param icon or
   * src for svg url
   */
  @Prop() icon!: string;
  @Prop() src?: string;
  /**
   * The size of the icon.
   * Available options are: `"small"`, `"medium"` `"large"`.
   */
  @Prop() size?: string;

  @Watch('icon')
  @Watch('src')
  loadIcon() {
    // Check if it's a named icon (from built-in icons)
    if (!isEmpty(this.icon)) {
      const svgContent = getIconMap().get(this.icon);
      if (svgContent) {
        // It's a built-in icon with inlined SVG
        this.svgContent = validateContent(document, svgContent, (this.el as any)['s-sc']);
        return;
      }
    }

    // Handle src attribute (custom URL)
    const url = getSrc(this.src);
    if (!isEmpty(url)) {
      getSvgContent(url).then(svgContent => {
        this.svgContent = validateContent(document, svgContent, (this.el as any)['s-sc']);
      });
    }
  }

  componentWillLoad() {
    this.loadIcon();
  }

  render() {
    let ret: HTMLElement;
    if (!isEmpty(this.svgContent)) {
      // we've already loaded up this svg at one point
      // and the svg content we've loaded and assigned checks out
      // render this svg!!
      ret = <div class="lar-icon-inner"><div innerHTML={this.svgContent}></div></div>;
    } else {
      // actively requesting the svg
      // or it's an SSR render
      // so let's just render an empty div for now
      ret = <div class="lar-icon-inner"></div>;
    }
    return (
      <Host
        role="img"
        class={{
        ...createColorClasses(this.color),
        [`lar-icon-${this.size}`]: (this.size === 'large' || this.size === 'small' || this.size === 'medium'),
        }}
      >
        {ret}
      </Host>
    );
  }
}

function getSrc(src: string | undefined) {
  if (typeof src === 'string') {
    src = src.trim();
    if (src.length > 0 && /(\/|\.)/.test(src)) {
      return src;
    }
  }
  return null;
}

const requests = new Map<string, Promise<string | null>>();

function getSvgContent(url: string) {
  // see if we already have a request for this url
  let req = requests.get(url);
  if (req === undefined) {
    // we don't already have a request
    req = fetch(url, { cache: 'force-cache' }).then(rsp => {
      if (rsp.ok) {
        return rsp.text();
      }
      return Promise.resolve(null);
    });

    // cache for the same requests
    requests.set(url, req);
  }

  return req;
}

function validateContent(
  document: Document,
  svgContent: string | null,
  scopeId: string | undefined
) {
  if (!isEmpty(svgContent)) {
    const frag = document.createDocumentFragment();
    const div = document.createElement('div');
    div.innerHTML = svgContent;
    frag.appendChild(div);

    // setup this way to ensure it works on our buddy IE
    for (let i = div.childNodes.length - 1; i >= 0; i--) {
      if (div.childNodes[i].nodeName.toLowerCase() !== 'svg') {
        div.removeChild(div.childNodes[i]);
      }
    }

    // must only have 1 root element
    const svgElm = div.firstElementChild;
    if (svgElm && svgElm.nodeName.toLowerCase() === 'svg') {
      if (!isEmpty(scopeId)) {
        svgElm.setAttribute('class', scopeId);
      }
      // root element must be an svg
      // lets double check we've got valid elements
      // do not allow scripts
      if (isValid(svgElm as any)) {
        return div.innerHTML;
      }
    }
  }
  return '';
}

function isValid(elm: HTMLElement) {
  if (elm.nodeType === 1) {
    if (elm.nodeName.toLowerCase() === 'script') {
      return false;
    }

    for (let i = 0; i < elm.attributes.length; i++) {
      const val = elm.attributes[i].value;
      if (typeof val === 'string' && val.toLowerCase().indexOf('on') === 0) {
        return false;
      }
    }

    for (let i = 0; i < elm.childNodes.length; i++) {
      if (!isValid(elm.childNodes[i] as any)) {
        return false;
      }
    }
  }
  return true;
}
